'use client';

import { PortableText } from '@portabletext/react';
import { GetStartedSection } from '@/types/sanity';
import { CTAButton } from '@/components/cta/CTAButton';
import { ScrollReveal } from '@/components/animations/ScrollAnimations';
import Image from 'next/image';
import { urlFor } from '@/sanity/lib/image';
import { cn } from '@/lib/utils';

interface GetStartedSectionComponentProps {
  section: GetStartedSection;
  pageSlug?: string;
}

export function GetStartedSectionComponent({ section, pageSlug }: GetStartedSectionComponentProps) {
  const bgStyle = section.backgroundImage?.asset
    ? {
        backgroundImage: `url(${urlFor(section.backgroundImage).width(1920).quality(80).url()})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined;

  return (
    <ScrollReveal direction="up" delay={0.1}>
      <section
        className={cn(
          'get-started-section relative py-8 md:py-12 lg:py-16 bg-primary text-primary-foreground',
          pageSlug && `get-started-section--${pageSlug}`
        )}
        style={bgStyle}
      >
        <div className="mx-auto max-w-5xl sm:px-6 lg:px-8 text-center">
          <div className="px-6 sm:px-0">
            {section.image?.asset?._ref && (
              <div className="mb-6 flex justify-center">
                <Image
                  src={urlFor(section.image).width(320).height(320).quality(80).url()}
                  alt={section.image?.alt || section.title}
                  width={160}
                  height={160}
                  className="rounded-full object-cover"
                />
              </div>
            )}
            <div className="mb-10">
              <h2 className="font-medium mb-4 text-section-title leading-section-title">
                {section.title}
              </h2>
              {section.subtitle && (
                <p className="opacity-90 text-section-subtitle leading-section-subtitle">
                  {section.subtitle}
                </p>
              )}
              {section.description && (
                <div className="prose prose-lg prose-invert max-w-2xl mx-auto mt-4 text-left opacity-90">
                  <PortableText value={section.description} />
                </div>
              )}
            </div>

            {section.ctas && section.ctas.length > 0 && (
              <div className="flex gap-4 justify-center flex-wrap">
                {section.ctas.map((cta) => (
                  <CTAButton
                    key={cta._id}
                    cta={cta}
                    variant="outline"
                    size="default"
                    className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}
